import { createFileRoute, Link } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { SidebarInspectionCard } from "@/components/service/SidebarInspectionCard";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";
import { Phone, MessageSquare, CheckCircle2 } from "lucide-react";

const SLUG = "/financing";
const TITLE = "Roofing Cost Solutions in Phoenix | Durafoam Roofing";
const DESCRIPTION =
  "Cost shouldn't delay a roof you need. Call Durafoam and we'll talk through practical solutions that fit your budget — no application, no obligation, just a straight conversation.";

const faqs = [
  {
    q: "Can I talk to you about the cost before committing to anything?",
    a: "Yes — that's exactly what this is. Call us or send your details and we'll talk through the work your roof needs and what's realistic for your budget. The conversation happens with your estimator after we've seen the roof, and there's no obligation.",
  },
  {
    q: "Can a repair be phased instead of done all at once?",
    a: "Often, yes. On many roofs we can stabilize the urgent area now and schedule the remaining work later, which spreads the expense without leaving the house exposed.",
  },
  {
    q: "Does the free inspection commit me to anything?",
    a: "No. You get a written assessment and an estimate, and you decide what to do with it. There's no obligation and no sales script.",
  },
  {
    q: "Should I call before or after getting a quote?",
    a: "Bring it up when you schedule. Knowing your budget up front lets us scope the work realistically instead of handing you a number that doesn't fit.",
  },
];

export const Route = createFileRoute("/financing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Roofing Cost Solutions",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: FinancingPage,
});

function FinancingPage() {
  return (
    <>
      <Breadcrumbs items={[{ href: "/financing", label: "Cost Solutions" }]} />

      <section className="bg-primary py-16 text-white lg:py-20">
        <Container className="max-w-3xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent">
            Let's talk
          </p>
          <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
            Let's Talk About Solutions for Your Roof
          </h1>
          <p className="mt-4 text-white/80">
            If cost is the reason you're putting off a roof you already know you need, give us a
            call. We'll talk through what the roof actually needs and how to make the timing work
            for your situation — no application, no sales script.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="#talk" variant="primary" size="lg">
              Let's Talk About Solutions
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              <span className="sm:hidden">Call Us</span>
              <span className="hidden sm:inline">Call 480-941-5373</span>
            </ButtonLink>
            <ButtonLink href="sms:+14809415373" variant="ghost-light" size="lg" className="sm:hidden">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Text Us
            </ButtonLink>
          </div>
        </Container>
      </section>

      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
            <div className="min-w-0 space-y-12">
              <Block heading="A Big Roof Bill Shouldn't Mean Delaying a Roof You Need">
                <p>
                  Most people who call us about cost aren't shopping. They already know the roof
                  needs work — an estimator has been out, or the ceiling stain is spreading — and
                  the number is the only thing standing in the way.
                </p>
                <p>
                  We understand that, and we'd rather have the budget conversation early than watch
                  a homeowner wait a year and end up paying for drywall, insulation, and framing on
                  top of the roof. In Phoenix, a small opening in a roof rarely stays small through
                  a monsoon season.
                </p>
                <p>
                  So bring it up. Tell your estimator what you can work with. It changes the
                  recommendation we make, and it's a conversation we have every week.
                </p>
              </Block>

              <Block heading="Solutions We Can Talk Through With You">
                <p>
                  Every roof and every household is different, so we don't publish a one-size
                  program. Here's what's usually on the table when we sit down with a homeowner:
                </p>
                <ul className="space-y-3">
                  {[
                    "Phasing the work — stabilize the urgent area now, schedule the rest for later, so the house is protected without taking on everything at once.",
                    "Deposit and completion terms — a portion at the start of work and the balance when the job is finished and you've walked it with us.",
                    "Scoping to your budget — repair versus recoat versus full replacement, with a straight answer about how much life each option realistically buys you.",
                    "Timing around your situation — insurance settlements, home sales, HOA approvals, or a bonus that lands next quarter.",
                  ].map((t) => (
                    <li key={t} className="flex gap-3">
                      <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  This is a conversation, not an application. There's nothing to fill out here —
                  call us or send your details and we'll talk it through.
                </p>
              </Block>

              <Block heading="This Applies to Full Roof Replacements and Larger Projects">
                <p>
                  The budget conversation makes the most sense when you're looking at a full roof
                  replacement or a larger commercial project — the kind of number where timing and
                  phasing actually matter:
                </p>
                <ul>
                  <li>
                    <strong>Full replacement:</strong> new{" "}
                    <Link to="/foam-roofing">foam roofing</Link>, or{" "}
                    <Link to="/tile-roofing">tile removal and re-roof</Link> when the underlayment
                    has aged out.
                  </li>
                  <li>
                    <strong>Commercial and multi-unit:</strong>{" "}
                    <Link to="/commercial-roofing">commercial roofing</Link>,{" "}
                    <Link to="/warehouse-roofing">warehouse roofing</Link>, and{" "}
                    <Link to="/hoa-roofing">HOA communities</Link>, where phased scheduling is often
                    the budget answer.
                  </li>
                </ul>
                <p>
                  For recoats, repairs, and smaller service calls, the cost is typically
                  straightforward — you're not looking at the kind of number that needs a
                  conversation. But if you're unsure whether your roof needs a repair or a full
                  replacement, that's exactly what the{" "}
                  <Link to="/free-roof-inspection-phoenix">free inspection</Link> settles.
                </p>
                <p>
                  Want to understand the ranges before you call? Our{" "}
                  <Link to="/roofing-costs-phoenix">Phoenix roofing cost guides</Link> lay out real
                  numbers by project type, plainly.
                </p>
              </Block>

              <Block heading="Why Talk to Us Before You Commit to Another Company">
                <p>
                  Cost pressure is exactly when homeowners get talked into the wrong scope — a full
                  replacement on a roof that needed a recoat, or a cheap patch on a roof that was
                  past saving.
                </p>
                <ul>
                  <li>
                    <strong>Family-owned and roofing the Valley since 1989.</strong> We're still
                    servicing roofs we installed in the 1990s.
                  </li>
                  <li>
                    <strong>Licensed Arizona ROC223900</strong> and fully insured.
                  </li>
                  <li>
                    <strong>Straight answers, not a sales script.</strong> If your roof has five
                    good years left, we'll tell you that instead of quoting a replacement.
                  </li>
                  <li>
                    <strong>Free written inspection with photos</strong> so you can see what we saw
                    and compare bids on equal footing.
                  </li>
                  <li>
                    <strong>Rosie on the House Certified Partner since 1996</strong> — see{" "}
                    <Link to="/rosie-on-the-house">why that matters</Link>.
                  </li>
                </ul>
              </Block>

              <div>
                <h2 className="text-2xl font-bold text-primary sm:text-3xl">
                  Frequently asked questions
                </h2>
                <dl className="mt-6 space-y-5">
                  {faqs.map((f) => (
                    <div key={f.q} className="rounded-xl border border-border bg-white p-5">
                      <dt className="font-semibold text-primary">{f.q}</dt>
                      <dd className="mt-2 text-[15px] leading-relaxed text-foreground/85">{f.a}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <p className="text-xs text-[var(--muted-foreground)]">
                Every situation is discussed individually and is subject to agreement before work
                begins. Ask your Durafoam estimator what's possible for your project.
              </p>
            </div>

            <div id="talk" className="lg:relative">
              <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1">
                <SidebarInspectionCard
                  eyebrow="No application, just a conversation"
                  heading="Let's talk about your situation"
                  subhead="Send your details and we'll call to talk through the work you need and how to make the timing work for you."
                  messagePlaceholder="What work do you need, and what are you trying to work around? (optional)"
                  submitLabel="Let's talk about solutions"
                  source="financing-inquiry"
                  serviceTag="Cost Solutions"
                />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-[var(--primary-deep)] py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold text-white sm:text-3xl">
            Give Us a Call — We'll Figure It Out Together
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/80">
            Call us and ask. We'll give you a straight read on what the roof needs, what it doesn't,
            and how to fit the work into what you can do right now.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="#talk" variant="primary" size="lg">
              Start the Conversation
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              <span className="sm:hidden">Call Us</span>
              <span className="hidden sm:inline">Call 480-941-5373</span>
            </ButtonLink>
            <ButtonLink href="sms:+14809415373" variant="ghost-light" size="lg" className="sm:hidden">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Text Us
            </ButtonLink>
          </div>
          <p className="mt-6 text-sm text-white/70">
            Or start with a{" "}
            <Link to="/free-roof-inspection-phoenix" className="underline">
              free roof inspection
            </Link>{" "}
            — no obligation either way.
          </p>
        </Container>
      </section>
    </>
  );
}

function Block({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-primary sm:text-3xl">{heading}</h2>
      <div className="prose-content mt-4 space-y-4 text-[16px] leading-relaxed text-foreground/85 [&_p]:m-0 [&_ul]:m-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:text-foreground/85">
        {children}
      </div>
    </div>
  );
}
